import type {
  CommonJobData as JobData,
  CommonJobLocation as JobLocation,
} from "../types/job";

function resolveLocation(data: JobData | JobLocation): JobLocation {
  if ("locations" in data) {
    return data.locations[0];
  }
  return data;
}

function resolveState(location: JobLocation): string {
  return location.city === location.state && location.county
    ? location.county
    : location.state;
}

/**
 * city, state, country
 * Merges city/state if the names are the same - eg: Bremen, Bremen
 * Merges state/county if the names are the same - eg: Hong Kong, Hong Kong, Hong Kong
 */
export function displayLocation(
  data: JobData | JobLocation,
  hideCountry?: boolean,
  hideState?: boolean
) {
  const location = resolveLocation(data);
  const state = resolveState(location);
  let string = location.city;

  if (location.city !== state && !hideState) {
    string += `, ${state}`;
  }

  if (state !== location.country && !hideCountry) {
    string += `, ${location.country}`;
  }

  return string;
}

/**
 * City (stateShort)
 *
 * E.g. Los Angeles (CA)
 */
export function displayLocationShort(
  data: JobData | JobLocation,
  hideState?: boolean
) {
  const location = resolveLocation(data);
  const countyReplacesDuplicateState =
    location.city === location.state && location.county;
  let state = countyReplacesDuplicateState
    ? location.county
    : location.stateShort;

  if (!state || state.length > 3) {
    state = countyReplacesDuplicateState ? location.county : location.state;
  }

  if (location.city?.includes(`(${state})`)) {
    return location.city;
  }
  return hideState ? `${location.city}` : `${location.city} (${state})`;
}

//ams OSRAM only wants to show the state if it's in the US, otherwise just show city and country

const USA_COUNTRIES = ["USA", "United States", "美国"];

const isUSACountry = (country: string) => USA_COUNTRIES.includes(country);

/**
 * City, Country
 * E.g. Essen, Deutschland
 *
 * Except when country = USA
 * E.g. Los Angeles, CA, USA
 */
export function displayLocationCountry(data: JobData | JobLocation) {
  const location = resolveLocation(data);
  if (isUSACountry(location.country)) {
    return `${location.city}, ${location.state}, ${location.country}`;
  }
  return `${location.city}, ${location.country}`;
}

/**
 * City (Country)
 *
 * E.g. Essen (Deutschland)
 *
 * Except when country = USA
 * E.g. Los Angeles (CA, USA)
 */
export function displayLocationShortCountry(data: JobData | JobLocation) {
  const location = resolveLocation(data);
  let state = location.stateShort;

  if (!state || state.length > 3) {
    state = location.state;
  }

  if (isUSACountry(location.country)) {
    return `${location.city} (${state}, ${location.country})`;
  }
  return `${location.city} (${location.country})`;
}
